import { useState } from 'react';
import { C } from '../data';
import { useAuth } from '../context/AuthContext';
import { checkLock, recordFailure, resetLock, MAX_TRIES } from '../utils/lockout';

const LOCK_KEYS = { login: 'lock_login', findId: 'lock_find_id', findPw: 'lock_find_pw' };

const maskUsername = (u) => {
  if (!u) return '';
  if (u.length <= 4) return u[0] + '*'.repeat(u.length - 1);
  return u.slice(0, 2) + '*'.repeat(u.length - 4) + u.slice(-2);
};

const lockMessage = (remainingMin) => `시도 횟수를 초과했습니다. ${remainingMin}분 후 다시 시도해 주세요.`;

export default function AuthModal({ onClose, onGoSignup }) {
  const { signIn, signInWithKakao, signInWithNaver, findUsername, requestPasswordReset } = useAuth();

  // view: 'login' | 'find-id' | 'find-id-result' | 'find-pw' | 'find-pw-sent'
  const [view, setView] = useState('login');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 로그인
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // 아이디 찾기
  const [fiName, setFiName] = useState('');
  const [fiEmail, setFiEmail] = useState('');
  const [fiResult, setFiResult] = useState(null);

  // 비밀번호 찾기
  const [fpUsername, setFpUsername] = useState('');
  const [fpEmail, setFpEmail] = useState('');

  const goto = (v) => { setView(v); setError(''); };

  const handleSignupClick = () => { onClose(); onGoSignup(); };

  /* ── 로그인 ── */
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    const lock = checkLock(LOCK_KEYS.login);
    if (lock.locked) { setError(lockMessage(lock.remainingMin)); return; }
    setLoading(true);
    try {
      const { error } = await signIn(loginUsername.trim(), loginPassword);
      if (error) throw error;
      resetLock(LOCK_KEYS.login);
      onClose();
    } catch (err) {
      const state = recordFailure(LOCK_KEYS.login);
      const msg = err.message;
      if (state.lockUntil) {
        setError(lockMessage(Math.ceil((state.lockUntil - Date.now()) / 60000)));
      } else {
        const remaining = MAX_TRIES - state.tries;
        const base =
          msg === 'Invalid login credentials' ? '아이디 또는 비밀번호가 올바르지 않습니다.' :
          msg === 'Email not confirmed'        ? '이메일 인증을 완료해 주세요. 메일함을 확인하세요.' :
          msg;
        setError(`${base}${msg === 'Invalid login credentials' ? ` (${remaining}회 남음)` : ''}`);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── 아이디 찾기 ── */
  const handleFindId = async (e) => {
    e.preventDefault();
    setError('');
    const lock = checkLock(LOCK_KEYS.findId);
    if (lock.locked) { setError(lockMessage(lock.remainingMin)); return; }
    setLoading(true);
    try {
      const username = await findUsername(fiName.trim(), fiEmail.trim());
      if (username) resetLock(LOCK_KEYS.findId);
      else recordFailure(LOCK_KEYS.findId);
      setFiResult(username);
      setView('find-id-result');
    } catch {
      setError('조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  /* ── 비밀번호 찾기 ── */
  const handleFindPw = async (e) => {
    e.preventDefault();
    setError('');
    const lock = checkLock(LOCK_KEYS.findPw);
    if (lock.locked) { setError(lockMessage(lock.remainingMin)); return; }
    setLoading(true);
    try {
      const { error } = await requestPasswordReset(fpUsername.trim(), fpEmail.trim());
      if (error) {
        recordFailure(LOCK_KEYS.findPw);
        throw error;
      }
      resetLock(LOCK_KEYS.findPw);
      setView('find-pw-sent');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ position:'fixed', inset:0, zIndex:5000, background:'rgba(26,26,26,.55)', display:'flex', alignItems:'center', justifyContent:'center', padding:20, animation:'fadeIn .2s ease', overflowY:'auto' }}
      onClick={onClose}
    >
      <div
        style={{ background:C.bg, width:'100%', maxWidth:400, padding:'40px 32px', position:'relative', animation:'fadeUp .25s ease', margin:'40px 0' }}
        onClick={e => e.stopPropagation()}
      >
        <button onClick={onClose} style={{ position:'absolute', top:14, right:18, background:'none', border:'none', fontSize:20, cursor:'pointer', color:C.muted, lineHeight:1 }}>✕</button>

        <div style={{ textAlign:'center', marginBottom:28 }}>
          <span style={{ fontFamily:"'DM Serif Display',serif", fontSize:20, letterSpacing:4 }}>UNDERLOOKED</span>
        </div>

        {/* 계정 찾기 헤더 */}
        {view !== 'login' && (
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:24 }}>
            <button onClick={() => goto('login')} style={{ background:'none', border:'none', cursor:'pointer', color:C.muted, fontSize:13 }}>←</button>
            <span style={{ fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:12, letterSpacing:1 }}>
              {view.startsWith('find-id') ? '아이디 찾기' : '비밀번호 찾기'}
            </span>
          </div>
        )}

        {/* ── 로그인 ── */}
        {view === 'login' && (
          <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              <label className="adm-label">아이디</label>
              <input className="adm-input" type="text" placeholder="아이디" value={loginUsername} onChange={e => setLoginUsername(e.target.value)} required />
            </div>
            <div>
              <label className="adm-label">비밀번호</label>
              <input className="adm-input" type="password" placeholder="비밀번호" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required />
            </div>

            {error && <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:12, color:'#C94040', textAlign:'center', lineHeight:1.5 }}>{error}</p>}

            <button type="submit" disabled={loading} style={submitBtnStyle(loading)}>{loading ? '처리 중...' : '로그인'}</button>

            <div style={{ display:'flex', justifyContent:'center', gap:10, marginTop:-2 }}>
              <button type="button" onClick={() => goto('find-id')} style={linkBtnStyle}>아이디 찾기</button>
              <span style={{ color:C.border }}>|</span>
              <button type="button" onClick={() => goto('find-pw')} style={linkBtnStyle}>비밀번호 찾기</button>
            </div>

            <button type="button" onClick={handleSignupClick} style={{
              marginTop:10, padding:'13px 0', background:'none', color:C.ink, border:`1px solid ${C.border}`, cursor:'pointer',
              fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:11, letterSpacing:3,
            }}>회원가입</button>
          </form>
        )}

        {/* ── 아이디 찾기 ── */}
        {view === 'find-id' && (
          <form onSubmit={handleFindId} style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              <label className="adm-label">이름</label>
              <input className="adm-input" type="text" value={fiName} onChange={e => setFiName(e.target.value)} required />
            </div>
            <div>
              <label className="adm-label">가입 시 등록한 이메일</label>
              <input className="adm-input" type="email" value={fiEmail} onChange={e => setFiEmail(e.target.value)} required />
            </div>
            {error && <p style={{ fontSize:12, color:'#C94040', textAlign:'center' }}>{error}</p>}
            <button type="submit" disabled={loading} style={submitBtnStyle(loading)}>{loading ? '조회 중...' : '아이디 찾기'}</button>
          </form>
        )}

        {view === 'find-id-result' && (
          <div style={{ textAlign:'center', padding:'12px 0 4px' }}>
            {fiResult ? (
              <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:14, color:C.ink, lineHeight:2 }}>
                회원님의 아이디는<br/><strong style={{ fontSize:18 }}>{maskUsername(fiResult)}</strong> 입니다.
              </p>
            ) : (
              <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:13, color:'#C94040', lineHeight:2 }}>일치하는 회원 정보가 없습니다.</p>
            )}
            <button onClick={() => goto('login')} style={{ marginTop:20, ...submitBtnStyle(false) }}>로그인으로 돌아가기</button>
          </div>
        )}

        {/* ── 비밀번호 찾기 ── */}
        {view === 'find-pw' && (
          <form onSubmit={handleFindPw} style={{ display:'flex', flexDirection:'column', gap:14 }}>
            <div>
              <label className="adm-label">아이디</label>
              <input className="adm-input" type="text" value={fpUsername} onChange={e => setFpUsername(e.target.value)} required />
            </div>
            <div>
              <label className="adm-label">가입 시 등록한 이메일</label>
              <input className="adm-input" type="email" value={fpEmail} onChange={e => setFpEmail(e.target.value)} required />
            </div>
            {error && <p style={{ fontSize:12, color:'#C94040', textAlign:'center' }}>{error}</p>}
            <button type="submit" disabled={loading} style={submitBtnStyle(loading)}>{loading ? '확인 중...' : '재설정 메일 받기'}</button>
          </form>
        )}

        {view === 'find-pw-sent' && (
          <div style={{ textAlign:'center', padding:'12px 0 4px' }}>
            <p style={{ fontFamily:"'Pretendard',sans-serif", fontSize:13, color:'#3D8B5C', lineHeight:2 }}>
              비밀번호 재설정 메일을 발송했습니다.<br/>메일함을 확인해 주세요.
            </p>
            <button onClick={() => goto('login')} style={{ marginTop:20, ...submitBtnStyle(false) }}>로그인으로 돌아가기</button>
          </div>
        )}

        {/* Divider + Social (로그인 화면에서만) */}
        {view === 'login' && (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:12, margin:'22px 0' }}>
              <div style={{ flex:1, height:1, background:C.border }} />
              <span style={{ fontFamily:"'Pretendard',sans-serif", fontSize:10, color:C.muted, letterSpacing:1 }}>간편 로그인</span>
              <div style={{ flex:1, height:1, background:C.border }} />
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              <SocialBtn color="#FEE500" textColor="#1A1A1A" icon={<KakaoIcon />} label="카카오로 시작하기" onClick={signInWithKakao} />
              <SocialBtn color="#03C75A" textColor="#fff" icon={<NaverIcon />} label="네이버로 시작하기" onClick={signInWithNaver} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const linkBtnStyle = { background:'none', border:'none', cursor:'pointer', fontFamily:"'Pretendard',sans-serif", fontSize:11, color:'#888', letterSpacing:.5 };
const submitBtnStyle = (loading) => ({
  padding:'13px 0', background:C.ink, color:C.bg, border:'none', cursor:'pointer', width:'100%',
  fontFamily:"'Pretendard',sans-serif", fontWeight:500, fontSize:11, letterSpacing:3,
  opacity:loading?.6:1, transition:'opacity .18s',
});

function SocialBtn({ color, textColor, icon, label, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display:'flex', alignItems:'center', justifyContent:'center', gap:10,
        padding:'12px 0', background:color, border:'none', cursor:'pointer',
        fontFamily:"'Pretendard',sans-serif", fontWeight:600, fontSize:13, color:textColor,
        opacity:hov?.85:1, transition:'opacity .18s',
      }}
    >
      {icon}
      {label}
    </button>
  );
}

function KakaoIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" fill="none">
      <ellipse cx="9" cy="7.5" rx="7.5" ry="6" fill="#1A1A1A"/>
      <path d="M9 13.5c-.5 0-.99-.04-1.46-.1L5 15.5l.72-2.68C4.08 11.73 3 9.71 3 7.5 3 3.91 5.69 1 9 1s6 2.91 6 6.5c0 3.59-2.69 6-6 6Z" fill="#1A1A1A"/>
    </svg>
  );
}

function NaverIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <path d="M8.66 7.8 6.1 3.75H3.75V11.25H6.34V7.2L8.9 11.25H11.25V3.75H8.66z" fill="white"/>
    </svg>
  );
}
